import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';

const WakeLockContext = createContext(null);

export const WakeLockProvider = ({ children }) => {
  const wakeLock = useRef(null);
  const isRequesting = useRef(false); // tracks if we WANT wake lock active

  const requestWakeLock = useCallback(async () => {
    isRequesting.current = true;

    // Try the native Screen Wake Lock API
    if ('wakeLock' in navigator) {
      try {
        if (wakeLock.current && !wakeLock.current.released) {
          return; // Already active
        }
        wakeLock.current = await navigator.wakeLock.request('screen');
        console.log('✅ Screen Wake Lock active (Native API)');
        wakeLock.current.addEventListener('release', () => {
          console.log('🔓 Native Wake Lock released');
        });
      } catch (err) {
        console.warn(`⚠️ Native Wake Lock failed (${err.name}). Fallback handled in UI.`);
      }
    } else {
      console.warn('⚠️ Native Wake Lock API not supported. Fallback handled in UI.');
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    isRequesting.current = false;

    // Release native wake lock
    if (wakeLock.current) {
      try {
        if (!wakeLock.current.released) {
          await wakeLock.current.release();
        }
      } catch (err) {
        console.error('❌ Error releasing Native Wake Lock:', err);
      }
      wakeLock.current = null;
    }
  }, []);

  useEffect(() => {
    // Re-acquire wake lock when page becomes visible again (e.g. after tab switch)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isRequesting.current) {
        await requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial attempt for native API
    requestWakeLock();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      releaseWakeLock();
    };
  }, [requestWakeLock, releaseWakeLock]);

  return (
    <WakeLockContext.Provider value={{ requestWakeLock, releaseWakeLock }}>
      {children}
    </WakeLockContext.Provider>
  );
};

export const useWakeLockContext = () => {
  const context = useContext(WakeLockContext);
  if (!context) {
    throw new Error('useWakeLockContext must be used within a WakeLockProvider');
  }
  return context;
};
